import { useState } from 'react'
import Code from '@/components/dashboard/Code';
import { Link } from 'react-router-dom';
import { CLIENT_CODE } from '@/lib/endpoints'
import { Clipboard, Check } from 'lucide-react';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

const OPENAPI_URL = 'https://axionquant.com/openapi.json';
const LLMS_TXT_URL = 'https://axionquant.com/llms.txt?v=2';



// Wrapper that just renders whichever icon element is passed in - the actual
// SVG markup lives per-library in the `libraries` object below.
function LibraryIcon({ icon, active }) {
  return (
    <div
      className={`w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
        active
          ? 'border-gray-900 bg-white'
          : 'border-gray-200 bg-white group-hover:border-gray-300'
      }`}
    >
      {icon}
    </div>
  );
}

// Blank placeholder - paste your real SVG's inner markup (paths/shapes) inside,
// or replace the whole element with e.g. <img src="/logos/python.svg" className="w-5 h-5" />
function BlankIcon({ label }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* TODO: paste icon markup for {label} here */}
    </svg>
  );
}

function ClientLibraries() {
  const libraries = {
    Python: {
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
          <path d="M11.956 0C5.928 0 6.29 2.596 6.29 2.596l.007 2.691h5.765v.808H3.932S0 5.632 0 11.72c0 6.087 3.363 5.872 3.363 5.872h2.009v-2.824s-.108-3.363 3.308-3.363h5.703s3.2.052 3.2-3.093V3.093S17.999 0 11.956 0zm-3.18 1.787a1.036 1.036 0 1 1 0 2.073 1.036 1.036 0 0 1 0-2.073z" fill="#3775A9" />
          <path d="M12.044 24c6.028 0 5.666-2.596 5.666-2.596l-.007-2.691H11.938v-.808h8.13S24 18.368 24 12.28c0-6.087-3.363-5.872-3.363-5.872h-2.009v2.824s.108 3.363-3.308 3.363H9.617s-3.2-.052-3.2 3.093v5.219S6.001 24 12.044 24zm3.18-1.787a1.036 1.036 0 1 1 0-2.073 1.036 1.036 0 0 1 0 2.073z" fill="#FFC331" />
        </svg>
      ),
      command: 'pip install axionquant-sdk',
      packageName: 'axionquant-sdk',
      packageUrl: 'https://pypi.org/project/axionquant-sdk/',
    },
    'Node.js': {
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#CC3534">
          <path d="M0 0v24h24V0H0zm19.2 19.2H12v-9.6H9.6v9.6H4.8V4.8h14.4v14.4z" />
        </svg>
      ),
      command: 'npm install @axionquant/sdk',
      packageName: '@axionquant/sdk',
      packageUrl: 'https://www.npmjs.com/package/@axionquant/sdk',
    },
    MCP: {
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16M4 12h10M4 18h7" className="text-violet-500" stroke="#7C3AED" />
          <circle cx="19" cy="16" r="3" stroke="#7C3AED" />
          <path d="M21.5 18.5l1.5 1.5" stroke="#7C3AED" />
        </svg>
      ),
      command: 'npm install @axionquant/mcp',
      packageName: '@axionquant/mcp',
      packageUrl: 'https://www.npmjs.com/package/@axionquant/mcp',
    },
  };

  const [active, setActive] = useState('Python');
  const [copied, setCopied] = useState(false);
  const lib = libraries[active];

  const handleCopy = () => {
    navigator.clipboard.writeText(lib.command);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="h-full flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <div className="flex-1 flex flex-col justify-center px-5 pt-5 pb-6">
        <p className="text-xs font-semibold tracking-wide text-gray-500 mb-5">CLIENT LIBRARIES</p>
        <div className="flex justify-center gap-10">
          {Object.keys(libraries).map((name) => (
            <button
              key={name}
              onClick={() => setActive(name)}
              className="group flex flex-col items-center gap-2 cursor-pointer"
            >
              <LibraryIcon icon={libraries[name].icon} active={active === name} />
              <span
                className={`text-sm pb-1.5 border-b-2 transition-colors ${
                  active === name
                    ? 'text-gray-900 border-gray-900 font-medium'
                    : 'text-gray-400 border-transparent group-hover:text-gray-600'
                }`}
              >
                {name}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 font-mono text-sm text-gray-700 min-w-0">
          <span className="text-gray-400 shrink-0">$</span>
          <span className="truncate">{lib.command}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <a
            href={lib.packageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 uppercase tracking-wide"
          >
            {lib.packageName}
          </a>
          <button
            onClick={handleCopy}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
            title="Copy to clipboard"
          >
            {copied ? <Check size={16} /> : <Clipboard size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenAPICallout() {
  const [copied, setCopied] = useState(null);

  const copy = (text, kind) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    });
  };

  const llmPrompt = `Here is the AxionQuant API spec: ${OPENAPI_URL}\n\nPlease help me understand the available endpoints and how to use them.`;

  return (
    <div className="relative h-full flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Thin top accent bar */}

      <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-5 px-6 py-5 flex-wrap min-h-[175px]">
        {/* Icon */}
        <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 border border-gray-200">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-gray-600">
            <path d="M3 5h14M3 10h10M3 15h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <circle cx="16" cy="14" r="3" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M18.5 16.5l1.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-[200px] m-h-[80px]">
          <p className="text-sm font-semibold text-gray-900 mb-0.5">OpenAPI Specification</p>
          <p className="text-sm text-gray-500 leading-snug">
            Machine-readable schema for every endpoint - import into Postman, Insomnia, or paste into any LLM.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <a
            href={OPENAPI_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg "
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 10L10 2M10 2H5M10 2V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Open
          </a>

          <button
            onClick={() => copy(OPENAPI_URL, 'url')}
            className="inline-flex items-center cursor-pointer gap-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors"
          >
            {copied === 'url' ? (
              <>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6.5l3 3 5-5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-green-600">Copied</span>
              </>
            ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <rect x="1" y="3" width="7" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M4 3V2a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                  Copy URL
                </>
              )}
          </button>

        </div>
      </div>

      {/* URL pill at bottom */}
      <div className="border-t border-gray-100 px-6 py-3 bg-gray-50 flex items-center gap-2">
        <span className="font-mono text-xs text-gray-400 select-all truncate">{OPENAPI_URL}</span>
      </div>
    </div>
  );
}

function LLMsTxtCallout() {
  const [copied, setCopied] = useState(null);

  const copy = (text, kind) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    });
  };

  return (
    <div className="relative h-full flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-5 px-6 py-5 flex-wrap min-h-[175px]">
        {/* Icon */}
        <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 border border-gray-200">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-gray-600">
            <path d="M3 5h14M3 10h14M3 15h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-gray-900 mb-0.5">llms.txt</p>
          <p className="text-sm text-gray-500 leading-snug">
            Machine-readable docs for LLMs - copy the link and paste it into any LLM to generate perfect code from open-ended prompts.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={() => copy(LLMS_TXT_URL, 'url')}
            className="inline-flex items-center cursor-pointer gap-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors"
          >
            {copied === 'url' ? (
              <>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6.5l3 3 5-5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-green-600">Copied</span>
              </>
            ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <rect x="1" y="3" width="7" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M4 3V2a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                  Copy URL
                </>
              )}
          </button>
        </div>
      </div>

      {/* URL pill at bottom */}
      <div className="border-t border-gray-100 px-6 py-3 bg-gray-50 flex items-center gap-2">
        <span className="font-mono text-xs text-gray-400 select-all truncate">{LLMS_TXT_URL}</span>
      </div>
    </div>
  );
}

export default function Introduction() {
  const [activeTab, setActiveTab] = useState('javascript');
  return (
    <>
      <SEOMetadata title="Introduction - API Overview" description="Overview of the AxionQuant API; market data, fundamentals and alternative data endpoints, authentication, and how to make your first request." canonicalUrl="https://axionquant.com/docs/00-overview/00-introduction" />
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <div className="max-w-[1400px]">
        <div className="max-w-[900px]">


          <p className="text-gray-700 leading-relaxed mb-4">
            <strong className="font-semibold">AxionQuant</strong> delivers market data, company fundamentals, financial statements, and alternative datasets through a fast, reliable REST API.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Get up and running in minutes with simple endpoints, predictable responses, and comprehensive coverage across equities, earnings, fundamentals, ownership data, and more. Focus on building your product, not cleaning data.
          </p>

        </div>


        {/* Developer Quickstart Card */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mt-14 mb-14 shadow-sm">
          <div className="flex flex-col lg:flex-row">
            {/* Left: text + buttons */}
            <div className="flex flex-col justify-center p-8 lg:w-[340px] lg:border-r border-gray-200 shrink-0">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Developer quickstart</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Make your first API request in minutes. Learn the basics of the AxionQuant platform.
              </p>
              <div className="flex gap-3">
                <Link
                  to="/docs/00-overview/01-getting-started"
                  className="bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Get started
                </Link>
                <Link
                  to="/dashboard/api-keys"
                  className="bg-white hover:bg-gray-50 text-gray-800 text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 transition-colors"
                >
                  Create API key
                </Link>
              </div>
            </div>


            {/* Right: code block */}
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* Tabs */}
              <div className="flex  px-6 pt-2">
                {['javascript', 'python', 'curl'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveTab(lang)}
                    className={`mr-4 cursor-pointer py-2 text-xs font-mono transition-colors ${
activeTab === lang
? 'border-b-2 border-blue-400'
: 'text-gray-400 hover:text-gray-500'
}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              {/* Code */}
              <div className="flex-1 p-6 overflow-x-auto">
                <Code lang={activeTab}>{CLIENT_CODE['stocks/prices'][activeTab]}</Code>
              </div>

            </div>
          </div>
        </div>


        <div className="flex flex-col lg:flex-row gap-6 mb-20 items-stretch">
          <div className="md:flex-1 md:min-w-0">
            <ClientLibraries />
          </div>
          <div className="md:flex-1 md:min-w-0">
            <OpenAPICallout />
          </div>
          <div className="md:flex-1 md:min-w-0">
            <LLMsTxtCallout />
          </div>
        </div>


        <div className="space-y-12">
          {/* Authentication Section */}
          <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px] w-[100%]">
            <div className="flex-1 lg:pr-6 xl:pr-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Authentication</h2>
              <p className="text-gray-700 mb-6">
                All API requests require authentication using your API key. You can obtain your API key from the <Link className="text-blue-400 underline" to={'/dashboard/api-keys'}> Dashboard </Link>
              </p>

              <div className="text-gray-700 mb-6 max-w-[900px] md:min-w-[300px]">
                <Code lang="bash">{` https://api.axionquant.com`}
              </Code>
            </div>


              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3">API Key Usage</h3>
                <p className="text-gray-700 mb-3">Include your API key in one of the following ways:</p>
                <div className="space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <p className="font-mono text-sm text-gray-800 mb-1">x-api-key: YOUR_API_KEY</p>
                    <p className="text-sm text-gray-600">HTTP Header (Recommended)</p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <p className="font-mono text-sm text-gray-800 mb-1">Authorization: Bearer YOUR_API_KEY</p>
                    <p className="text-sm text-gray-600">Authorization Header</p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <p className="font-mono text-sm text-gray-800 mb-1">?apiKey=YOUR_API_KEY</p>
                    <p className="text-sm text-gray-600">Query Parameter</p>
                  </div>
                </div>
              </div>

            </div>

            <div className=" lg:max-w-[800px]">
              <div className="rounded-lg md:p-4 py-4 lg:sticky lg:top-0">
                <h3 className="text-sm font-semibold mb-3">Authentication Examples</h3>

                <div className="mb-4">
                  <p className="text-gray-400 text-xs mb-2">JavaScript (Fetch API)</p>
                  <Code lang='javascript'>{`async function fetchStockData(ticker) {
  const response = await fetch(\`https://api.axionquant.com/stocks/\${ticker}\`, {
  headers: { 'x-api-key': 'your-api-key-here' }
});
return await response.json();
}`}</Code>
                </div>

                <div className="mb-4">
                  <p className="text-gray-400 text-xs mb-2">Python (Requests)</p>
                  <Code lang='python'>{`import requests
def get_stock_data(ticker, api_key):
url = f"https://api.axionquant.com/stocks/{ticker}"
params = {"apiKey": api_key}
return requests.get(url, params=params).json()
`}</Code>
                </div>

                <div className="mb-4">
                  <p className="text-gray-400 text-xs mb-2">Bash (cURL)</p>
                  <Code lang='bash'>{`curl -X GET "https://api.axionquant.com/stocks/AAPL"
-H "Authorization: Bearer YOUR_API_KEY"`}</Code>
                </div>
              </div>
            </div>
          </div>



        </div>
      </div>
    </div>
    </>
  );
}
